import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { Users, DollarSign, TrendingUp, LogOut, Crown } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function StaffPortalPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/api/staff-portal/me');
      setData(res.data);
    } catch (err) {
      console.error('Staff portal error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
      <div className="spinner" />
    </div>
  );

  const summary = data?.summary || {};
  const players = data?.players || [];
  const staff = data?.staff || {};

  return (
    <div className="min-h-[100dvh]" style={{ background: '#0B0E14' }} data-testid="staff-portal-page">
      <header
        className="flex flex-col gap-3 border-b border-white/10 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between"
        style={{
          paddingTop: 'max(1rem, env(safe-area-inset-top))',
        }}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          <Crown className="h-6 w-6 shrink-0 text-[#D4AF37]" />
          <h1 className="min-w-0 text-lg font-bold text-white sm:text-xl">Staff Referral Portal</h1>
          <span className="max-w-full truncate rounded-full bg-[#D4AF37]/20 px-2 py-1 text-xs text-[#D4AF37]">
            {staff.referral_code}
          </span>
        </div>
        <div className="flex min-w-0 items-center justify-between gap-3 lg:justify-end">
          <span className="min-w-0 truncate text-sm text-gray-400">Welcome, {staff.username || user?.username}</span>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="staff-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-card rounded-xl p-5" data-testid="referred-players-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-sm text-gray-400">Referred Players</span>
            </div>
            <p className="text-3xl font-bold text-white">{summary.referred_players || 0}</p>
          </div>

          <div className="glass-card rounded-xl p-5" data-testid="first-deposit-count-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-sm text-gray-400">First-Time Deposits</span>
            </div>
            <p className="text-3xl font-bold text-white">{summary.first_time_deposit_count || 0}</p>
          </div>

          <div className="glass-card rounded-xl p-5" data-testid="first-deposit-total-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <span className="text-sm text-gray-400">First-Time Deposit Total</span>
            </div>
            <p className="text-3xl font-bold text-white">${(summary.first_time_deposit_total || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Referred Players Table */}
        <div className="glass-card rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white mb-4">Referred Players</h2>
          {players.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No referred players yet. Share your referral code to get started.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]" data-testid="referred-players-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">Username</th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-sm text-gray-400 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => (
                    <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{p.username}</td>
                      <td className="py-3 px-4 text-gray-400">{p.email}</td>
                      <td className="py-3 px-4 text-gray-400">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
